import { useId } from 'react';

import clsx from 'clsx';
import dynamic from 'next/dynamic';

import { useGlobal } from '@hultafors/hultaforsgroup/hooks';
import {
  type ArticleListItem,
  isNewsStory,
  isReportListing,
} from '@hultafors/hultaforsgroup/types';

import { ArticleItem } from '../article-item/article-item';
import { Button } from '../button/button';

import styles from './article-list.module.scss';

const HTag = dynamic(() =>
  import('@hultafors/shared/components').then((mod) => mod.HTag),
);

const Grid = dynamic(() =>
  import('@hultafors/shared/components').then((mod) => mod.Grid),
);

const GridChild = dynamic(() =>
  import('@hultafors/shared/components').then((mod) => mod.GridChild),
);

interface ArticleListProps {
  items: ArticleListItem[];
  title?: string | null;
  buttonText?: string | null;
  buttonOnClick?: React.MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
  showLoadMore?: boolean;
  largeTitle?: boolean;
  children?: React.ReactNode;
  nrOfItems?: number;
}

export const ArticleList: React.FC<ArticleListProps> = ({
  title,
  items,
  showLoadMore,
  buttonOnClick,
  loading,
  buttonText,
  children,
  largeTitle,
  nrOfItems,
}) => {
  const uid = useId();
  const { globalContent } = useGlobal();

  const itemMapper = (item: ArticleListItem) => {
    if (!item) return null;

    const isReports = isReportListing(item);
    const isNews = isNewsStory(item);
    const image = isReports || isNews ? item.image : null;

    return (
      <GridChild
        key={`ArticleList-${uid}-${item.id}`}
        columnSpan={[
          { columns: 12 },
          { breakpoint: 'tablet', columns: 6 },
          { breakpoint: 'desktop', columns: 4 },
        ]}
      >
        <ArticleItem item={item} image={image} asThumbnail />
      </GridChild>
    );
  };

  if (!items?.length) {
    return null;
  }

  return (
    <section
      className={clsx('content-container', 'wide', styles['wrapper'])}
      data-test="NewsArticleList"
    >
      <HTag
        type="h2"
        styleType={largeTitle ? 'h1' : 'h2'}
        className={styles['title']}
      >
        {title}
      </HTag>
      <Grid
        columns={[{ columns: 1 }, { breakpoint: 'mobileMax', columns: 12 }]}
        maxWidth="var(--grid-max-width)"
        margin={'var(--grid-margin)'}
        rowGap={16}
        className={styles['grid']}
      >
        {items?.slice(0, nrOfItems).map(itemMapper)}
      </Grid>
      {showLoadMore && (
        <Button
          label={buttonText}
          onClick={buttonOnClick}
          loading={loading}
          loadingLabel={globalContent?.loading}
          center
          variant={'outline'}
        />
      )}
      {children}
    </section>
  );
};
